import { FieldArray, Formik, useFormikContext } from "formik";
import { useEffect } from "react";
import apiRequest from "../../../../utils/towerHeadAdmin/apiRequest";
import InputField from "../../../ui/form/InputField";
import Button from "../../../ui/utils/Button";
import SelectField from "../../../ui/form/SelectField";
import usePost from "../../../../hooks/api/usePost";
import useFetch from "../../../../hooks/api/useFetch";
import Alert from "../../../ui/utils/Alert";
import { adminTowerUpdateValidationSchema } from "../../../../validation/towerHeadAdmin/authValidation";
import { CgAdd } from "react-icons/cg";
import { Trash } from "lucide-react";


const LocationWatcher = ({ fetchStates, fetchCities, setBlankResponse, setCityBlankResponse }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });
      setFieldValue("stateId", "");
    } else {
      setBlankResponse();
    }
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });
      setFieldValue("cityId", "");
    } else {
      setCityBlankResponse();
    }
  }, [values.stateId]);

  return null;
};


const EditStaffAdmin = ({ closeModal, callback, data }) => {
  const { error, isLoading: loading, post, serverErrors } = usePost(apiRequest.editCorporateAdmin);

  console.log(data);
  

  const { data: adminList } = useFetch({
    apiMethod: apiRequest.getHeadAdmin,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: true,
  });

  const initialValues = {
    name: data?.name || "",
    countryId: data?.countryId?._id || "",
    cityId: data?.cityId?._id || "",
    stateId: data?.stateId?._id || "",
    corporateCodes: data?.corporateCodes || [{ adminId: "", code: "" }],
    mobile: data?.mobile || "",
    email: data?.email || "",
    contactPerson: data?.contactPerson || "",
    password: "",
    userId: data?.userId || "",
  };

  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: false,
  });

  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Edit Corporate Admin</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={adminTowerUpdateValidationSchema}
        onSubmit={async (values) => {
          const response = await post({ data: { body: values, id: data?._id } });

          if (response.response) {
            callback?.();
          }
        }}
      >
        {({ values, handleBlur, handleChange, handleSubmit, touched, errors }) => (
          <>
            <LocationWatcher
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />

            <form onSubmit={handleSubmit} className="text-sm max-h-[80vh] overflow-auto">
              {error && <Alert message={error} type="error" />}

              <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2">

                <InputField
                  name="name"
                  label="Corporate Admin Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && errors.name}
                  serverError={serverErrors?.name}
                />

                <InputField
                  name="mobile"
                  label="Mobile"
                  value={values.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.mobile && errors.mobile}
                  serverError={serverErrors?.mobile}
                />

                <InputField
                  name="email"
                  label="Email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && errors.email}
                  serverError={serverErrors?.email}
                />

                <SelectField
                  name="countryId"
                  label="Country"
                  value={values.countryId}
                  onBlur={handleBlur}
                  error={touched.countryId && errors.countryId}
                  serverError={serverErrors?.countryId}
                  onChange={handleChange}
                  options={countryData?.countries?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                />

                <SelectField
                  name="stateId"
                  label="State"
                  value={values.stateId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.stateId && errors.stateId}
                  serverError={serverErrors?.stateId}
                  options={stateData?.states?.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                />

                <SelectField
                  name="cityId"
                  label="City"
                  value={values.cityId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.cityId && errors.cityId}
                  serverError={serverErrors?.cityId}
                  options={cityData?.cities?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                />

                <InputField
                  name="contactPerson"
                  label="Contact Person"
                  value={values.contactPerson}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactPerson && errors.contactPerson}
                  serverError={serverErrors?.contactPerson}
                />

                <InputField
                  name="userId"
                  label="User ID"
                  value={values.userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.userId && errors.userId}
                  serverError={serverErrors?.userId}
                />

                <InputField
                  type="password"
                  name="password"
                  label="Password (Leave empty to keep old)"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && errors.password}
                  serverError={serverErrors?.password}
                />

                {/* Corporate Codes */}
                <div className="col-span-full">
                  <FieldArray name="corporateCodes">
                    {({ push, remove }) => (
                      <>
                        <table className="w-full border border-collapse border-gray-300 text-left">
                          <thead>
                            <tr>
                              <th className="p-2 border">Admin Head</th>
                              <th className="p-2 border">Corporate Code</th>
                              <th className="p-2 border">Actions</th>
                            </tr>
                          </thead>

                          <tbody>
                            {values.corporateCodes.map((row, index) => {
                              const rowAdmin = adminList?.admins?.find(
                                (a) => a._id === row.adminId
                              );

                              const filteredCodes = rowAdmin?.corporateCode?.filter(
                                (code) =>
                                  !values.corporateCodes.some(
                                    (item, i) => i !== index && item.code === code.value
                                  )
                              );

                              return (
                                <tr key={index}>
                                  <td className="p-2 border">
                                    <SelectField
                                      name={`corporateCodes.${index}.adminId`}
                                      value={row.adminId}
                                      onChange={(e) => {
                                        handleChange(e);
                                        values.corporateCodes[index].code = "";
                                      }}
                                      options={adminList?.admins?.map((a) => ({
                                        value: a._id,
                                        label: a.name,
                                      }))}
                                    />
                                  </td>

                                  <td className="p-2 border">
                                    <SelectField
                                      name={`corporateCodes.${index}.code`}
                                      value={row.code}
                                      onChange={handleChange}
                                      options={filteredCodes?.map((code) => ({
                                        value: code.value,
                                        label: `${code.name} (${code.value})`,
                                      }))}
                                    />
                                  </td>

                                  <td className="p-2 border text-center">
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-500 bg-red-50 p-2 rounded-lg hover:text-white hover:bg-red-500"
                                    >
                                      <Trash size={18} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        <button
                          type="button"
                          onClick={() => push({ adminId: "", code: "" })}
                          className="mt-2 px-2 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-2"
                        >
                          <CgAdd /> Add Row
                        </button>
                      </>
                    )}
                  </FieldArray>
                </div>
              </div>

              <div className="border-b" />

              <div className="flex justify-end gap-2 p-4">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={loading}>
                  Update
                </Button>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default EditStaffAdmin;
















